import PageWrapper from "../../components/common/PageWrapper";
import Header from "../../components/common/Header";
import BodyWrapper from "../../components/common/BodyWrapper";
import Footer from "../../components/common/Footer";
import Wrapper from "../../components/dashboard/dashboardPartials/Wrapper";
import Dashboard from "../../components/dashboard/dashboardPartials/Dashboard";
import { useGeneral } from "../../components/contexts/GeneralContext";
import { useEffect, useState } from "react";
import axios from "../../config/axios";
import FullScreenLoading from "../../components/common/loading/FullScreenLoading";
import ApiErrorMessage from "../../components/common/ApiErrorMessage";
import NoItemsFound from "../../components/common/NoItemsFound";
import MySubscriptions from "../../components/dashboard/dashboardPartials/MySubscriptions";
import GeneralButton from "../../components/common/GeneralButton";
import SearchBar from "../../components/common/SearchBar";
import SelectBox from "../../components/common/SelectBox";
import { useLocation } from "react-router-dom";
import { formatToTitleValue } from "../../components/utils/commonHelper";

// ✅ Auto-scroll on initial load
const useScrollToTop = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);
};

// ✅ Main Page Component
const MySubscriptionsPage = () => {
  const location = useLocation();
  const getFiltersFromURL = () => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get("search") || "",
      sort: params.get("sort") || "",
      status: params.get("status") || "",
      planId: params.get("planId") || "",
      mealId: params.get("mealId") || "",
    };
  };

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(getFiltersFromURL);
  const [inputValue, setInputValue] = useState(filters.search || "");
  const [content, setContent] = useState({
    items: [],
    pagination: {},
    plans: [],
    meals: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { discountCoupons } = useGeneral();

  useScrollToTop();

  // ✅ Function passed down to MySubscriptions
  const fetchSubscriptions = async (pageNum = 1, appliedFilters = filters) => {
    try {
      setLoading(true);
      const params = { page: pageNum };
      if (appliedFilters.search) params.q = appliedFilters.search;
      if (appliedFilters.sort) params.sort = appliedFilters.sort;
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.planId) params.planId = appliedFilters.planId;
      if (appliedFilters.mealId) params.mealId = appliedFilters.mealId;
      const { data } = await axios.get("/dashboard/subscriptions", { params });

      setContent((prev) => ({
        items: pageNum === 1 ? data.items : [...prev.items, ...data.items],
        pagination: data.pagination,
        plans: data.plans,
        meals: data.meals,
      }));

      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to load subscriptions. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    fetchSubscriptions(1, filters);
    setPage(1);
  }, [filters]);

  const handleClearSearch = () => {
    setInputValue("");
    setFilters((prev) => ({ ...prev, search: "" }));
  };

  const handleSearch = (searchQuery) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, search: searchQuery }));
  };

  const handleSortChange = (val) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, sort: val }));
  };

  const handlePlanChange = (val) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, planId: val }));
  };
  const handleMealChange = (val) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, mealId: val }));
  };
  const handleStatusChange = (val) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, status: val }));
  };

  const handleNext = () => {
    if (content?.pagination?.pageCount && page < content.pagination.pageCount) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchSubscriptions(nextPage, filters);
    }
  };

  const sortOptions = [
    { label: "Newest to Oldest", value: "1" },
    { label: "Oldest to Newest", value: "-1" },
  ];

  return (
    <>
      {loading && page === 1 && <FullScreenLoading />}
      <PageWrapper loading={false} className="bg-bg min-h-screen">
        <Header code={discountCoupons} />

        <BodyWrapper className="mb-0 xl:mb-0">
          <Wrapper
            component={
              <>
                {content?.pagination?.totalItems > 0 && (
                  <div className="mb-4 flex flex-col items-start md:items-center justify-between gap-2 md:mt-2 mt-0">
                    <div className="flex-1 flex flex-col md:flex-row items-center justify-end gap-2 w-full">
                      <div className="flex flex-col md:flex-row items-center justify-end gap-4 w-full xl:w-[88%]">
                        <SearchBar
                          value={inputValue}
                          setValue={setInputValue}
                          onClear={handleClearSearch}
                          onSearch={handleSearch}
                        />

                        <SelectBox
                          value={filters.sort}
                          setValue={handleSortChange}
                          options={sortOptions}
                          placeholder="Sort By"
                        />
                        <SelectBox
                          value={filters.planId}
                          setValue={handlePlanChange}
                          options={formatToTitleValue(content?.plans)}
                          placeholder="Select Plan"
                        />
                        <SelectBox
                          value={filters.mealId}
                          setValue={handleMealChange}
                      options={formatToTitleValue(content?.meals)}
                          placeholder="Select Meal"
                        />
                        <SelectBox
                          value={filters.status}
                          setValue={handleStatusChange}
                          options={[
                            { label: "Active", value: "Active" },
                            { label: "Paused", value: "Paused" },
                            { label: "Cancelled", value: "Cancelled" },
                            { label: "Expired", value: "Expired" },
                          ]}
                          placeholder="Select Status"
                        />
                      </div>
                    </div>

                    {(filters.search || filters.status || filters.sort || filters.planId || filters.mealId) && (
                      <div className="flex flex-wrap items-center gap-2 mt-2 px-3 py-2 bg-[#D06F36]/10 rounded-lg w-full">
                        {filters.search && (
                          <div className="flex items-center gap-1 text-orange me-2 py-1 rounded text-sm text-gray-700">
                            <span className="text-secondary"> Search: </span>
                            <span className="text-[#D06F36]">
                              "{filters.search}"
                            </span>
                            <button
                              onClick={() =>
                                setFilters({ ...filters, search: "" })
                              }
                              title="Click to remove search"
                              className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        {filters.status && (
                          <div className="flex items-center gap-1 text-orange me-2 py-1 rounded text-sm text-gray-800">
                            <span className="text-secondary">Filter: </span>
                            {filters.status}
                            <button
                              onClick={() =>
                                setFilters({ ...filters, status: "" })
                              }
                              title="Click to remove status"
                              className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                            >
                              ×
                            </button>
                          </div>
                        )}
                         {filters.planId && (
                          <div className="flex items-center gap-1 text-orange me-2 py-1 rounded text-sm text-gray-800">
                            <span className="text-secondary">Filter: </span>
                          {
                              formatToTitleValue(content?.plans).find((s) => s.value === filters.planId)
                                ?.label
                            }
                            <button
                              onClick={() =>
                                setFilters({ ...filters, planId: "" })
                              }
                              title="Click to remove status"
                              className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        {filters.mealId && (
                          <div className="flex items-center gap-1 text-orange me-2 py-1 rounded text-sm text-gray-800">
                            <span className="text-secondary">Filter: </span>
                             {
                              formatToTitleValue(content?.meals).find((s) => s.value === filters.mealId)
                                ?.label
                            }
                            <button
                              onClick={() =>
                                setFilters({ ...filters, mealId: "" })
                              }
                              title="Click to remove status"
                              className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        {filters.sort && (
                          <div className="flex items-center gap-1 text-orange me-2 py-1 rounded text-sm text-gray-800">
                            <span className="text-secondary">Sort: </span>
                            {
                              sortOptions.find((s) => s.value === filters.sort)
                                ?.label
                            }
                            <button
                              onClick={() =>
                                setFilters({ ...filters, sort: "" })
                              }
                              title="Click to remove sort"
                              className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        <button
                          onClick={() => {
                            handleClearSearch();
                            setFilters({ sort: "", status: "", planId:"", mealId:"" });
                          }}
                          className="ml-auto text-xs text-[#D06F36] font-medium hover:underline transition-all duration-200"
                        >
                          Clear All
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {!loading && error ? (
                  <ApiErrorMessage
                    message1="Failed to load subscriptions"
                    message2="Please check your connection and try again."
                  />
                ) : !loading && !content?.items?.length ? (
                  <NoItemsFound
                    message1="No Subscriptions Found"
                    message2={
                      filters.search || filters.status
                        ? "Try a different query or filter"
                        : "Subscribe to explore available meal plans."
                    }
                  />
                ) : (
                  <>
                    {/* ✅ Now MySubscriptions can trigger API */}
                    <MySubscriptions
                      account={content.items}
                      fetchSubscriptions={fetchSubscriptions}
                      filters={filters}
                      page={page}
                      setPage={setPage}
                      loading={loading}
                    />

                    {content.pagination?.pageCount > 1 &&
                      page < content.pagination.pageCount && (
                        <div className="col-span-full flex justify-center w-full mt-2 xl:mt-4">
                          <GeneralButton
                            text={loading ? "Loading..." : "Load More"}
                            title="Click to load more items"
                            onClick={handleNext}
                            disabled={loading}
                            bgColor="#D06F36"
                          />
                        </div>
                      )}

                    {loading && page > 1 && (
                      <div className="col-span-full flex justify-center mt-3">
                        <div className="w-5 h-5 border-4 border-tertiary border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </>
                )}
              </>
            }
          />
        </BodyWrapper>

        <Footer />
      </PageWrapper>
    </>
  );
};

export default MySubscriptionsPage;
